import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import export_icon from "../../assets/images/export_icon.svg";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import BonusIncentiveGraph from "../../components/Graph/BonusIncentiveGraph";
import FilterTopBar from "../../components/navigation/FilterTopBar";

import {
  fetchDashboardSummary,
  fetchDashboardBonus,
  fetchAllClients,
  fetchAllCompanies,
  fetchAllEmployees,
  exportCompaniesCSV,
  exportEmployeesCSV,
} from "../../store/slices/userSlice";

import ClientsListTable from "../../components/tables/ClientsListTable";
import CompaniesListTable from "../../components/tables/CompanyListTable";
import EmployeesListTable from "../../components/tables/EmployeListTable";

import TopHeadding from "../../components/layout/TopHeadding";
import Widgets from "../../components/widgets/Widgets";

import progres_chart_dashboard from "../../assets/images/progres_chart_dashboard.png";
import filter_dropdown from "../../assets/images/filter_dropdown.svg";
import plus_icon from "../../assets/images/plus_icon.svg";
import search_icon_new from "../../assets/images/search_icon_new.svg";

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 6;

  const getCurrentMonthRange = () => {
    const now = new Date();
    return {
      startDate: null,
      endDate: null,
    };
  };

  const [{ startDate, endDate }, setSelectedRange] = useState(
    getCurrentMonthRange(),
  );

  const DASHBOARD_LIST_KEY = "dashboardListing";

  const { user, role, dashboardSummary, exportCSVLoading, dashboardBonus } =
    useAppSelector((s) => s.user);

  const roleData = useAppSelector(
    (s) => s.user.listDataByKey[DASHBOARD_LIST_KEY] || [],
  );
  const loading = useAppSelector(
    (s) => s.user.listLoadingByKey[DASHBOARD_LIST_KEY] || false,
  );
  const totalRecords = useAppSelector(
    (s) => s.user.totalByKey[DASHBOARD_LIST_KEY] || 0,
  );

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    payRange: [0, 1000000],
    status: true,
  });

  const buildCompanyParams = (extra = {}) => {
    const params = {
      startDate: filters.startDate ?? null,
      endDate: filters.endDate ?? null,
      status: filters.status,
      minPayAvg: filters.payRange[0],
      maxPayAvg: filters.payRange[1],
      ...extra,
    };

    if (search) {
      params.search = search;
    }

    if (role === "Client") {
      params.clientId = user?.id;
    } else if (role === "Company") {
      params.companyId = user?.id;
    }

    return params;
  };

  useEffect(() => {
    const maxPage = Math.ceil(totalRecords / pageSize) || 1;
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [totalRecords, page, pageSize]);

  useEffect(() => {
    if (!user || !role) return;

    const timeout = setTimeout(() => {
      const params = buildCompanyParams({
        pageIndex: page - 1,
        pageSize: pageSize,
      });
      if (role === "SuperAdmin") {
        dispatch(fetchAllClients({ params, key: DASHBOARD_LIST_KEY }));
      } else if (role === "Client") {
        dispatch(fetchAllCompanies({ params, key: DASHBOARD_LIST_KEY }));
      } else if (role === "Company") {
        dispatch(fetchAllEmployees({ params, key: DASHBOARD_LIST_KEY }));
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [user, role, search, dispatch, page, filters]);

  useEffect(() => {
    if (!user || !role) return;

    const timeout = setTimeout(() => {
      const filters = {
        startDate,
        endDate,
        search,
      };

      if (role === "Client") filters.clientId = user.id;
      else if (role === "Company") filters.companyUserId = user.id;

      dispatch(fetchDashboardSummary(filters));
      if (role === "SuperAdmin") dispatch(fetchDashboardBonus());
    }, 400);

    return () => clearTimeout(timeout);
  }, [user, role, startDate, endDate, search, page, dispatch]);

  const handleExportCSV = () => {
    const params = buildCompanyParams();
    if (role === "Client") {
      dispatch(exportCompaniesCSV(params));
    } else if (role === "Company") {
      dispatch(exportEmployeesCSV(params));
    }
  };

  const TableComponent =
    role === "SuperAdmin"
      ? ClientsListTable
      : role === "Client"
        ? CompaniesListTable
        : EmployeesListTable;

  const tableHeading =
    role === "SuperAdmin"
      ? "Our Clients"
      : role === "Client"
        ? "Our Companies"
        : "Our Employee";

  const addButtonRoute =
    role === "SuperAdmin"
      ? "/addnewclient"
      : role === "Client"
        ? "/addcompany"
        : "/addnewemployee";

  const viewAllRoute =
    role === "SuperAdmin"
      ? "/client"
      : role === "Client"
        ? "/companies"
        : "/employes";

  const showGraph = role === "SuperAdmin";

  return (
    <div className="dashboard">
      <TopHeadding
        title="Dashboard"
        subtitle={`Welcome, ${user?.name}!`}
        showDatepicker={true}
        onDateChange={setSelectedRange}
        buttons={[
          {
            text:
              role === "SuperAdmin"
                ? "Add Client"
                : role === "Client"
                  ? "Add Company"
                  : "Add Employee",
            className: "btn_primary",
            icon: plus_icon,
            onClick: () =>
              navigate(addButtonRoute, {
                state: { from: location.pathname, id: user?.id },
              }),
          },
        ]}
      />

      <Widgets summary={dashboardSummary} role={role} user={user} />

      <div className="clint_table mt_30">
        <div className="container-fluid">
          <div className="row match-height-row">
            {showGraph && (
              <div className="col-md-4">
                <div className="white_box graph_box">
                  <div className="top_bar px_20 py_20 d-flex">
                    <h4 className="mb-0">Bonuses & Incentives</h4>
                    <img src={filter_dropdown} className="ms-auto" alt="" />
                  </div>
                  <div className="w-100 px_20 py_20">
                    {/* <img src={progres_chart_dashboard} alt="" /> */}
                    <BonusIncentiveGraph
                      bonus={dashboardBonus?.bonusTotal || 0}
                      incentive={dashboardBonus?.incentiveTotal || 0}
                      total={dashboardBonus?.total || 0}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className={showGraph ? "col-md-8" : "col-md-12"}>
              <div className="white_box table_box py_20 px_20">
                {role === "SuperAdmin" && (
                  <div className="top_bar d-flex align-items-center mb_15">
                    <h4 className="mb-0">
                      {tableHeading} ({roleData.length})
                    </h4>

                    <div className="d-flex align-items-center ms-auto gap-3 flex-nowrap">
                      <button
                        className="btn_link ms-3"
                        style={{ border: "none", background: "transparent" }}
                        onClick={() => navigate(viewAllRoute)}
                      >
                        View All
                      </button>

                      <div className="search_field">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                        />
                        <span className="serach_icon">
                          <img src={search_icon_new} alt="Search" />
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {(role === "Client" || role === "Company") && (
                  <FilterTopBar
                    title={tableHeading}
                    count={roleData.length}
                    search={search}
                    onSearchChange={setSearch}
                    placeholder={
                      role === "Client" ? "Search Company" : "Search Employee"
                    }
                    onFilterChange={setFilters}
                    showExport={false}
                    showDashboardExport={true}
                    exportCSVLoading={exportCSVLoading}
                    handleExportCSV={() => handleExportCSV()}
                    searchClassName="ms-auto"
                  />
                )}

                <TableComponent
                  wrapBox={false}
                  heading={tableHeading}
                  data={roleData}
                  role={role}
                  loading={loading}
                  showTopBar={false}
                  viewAllLink={viewAllRoute}
                  initialSearch={search}
                  currentPage={page}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={(pageNumber) => setPage(pageNumber)}
                  isDetailPage={true}
                  pageKey={DASHBOARD_LIST_KEY}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
